import { useState, useEffect } from 'react';

// Material UI
import { styled, Typography, Grid, Box, IconButton } from '@mui/material';
import { Card, CardHeader, CardContent, CardActions } from '@mui/material';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

// Components
import ConfirmAction from 'components/popups/ConfirmAction';
import { ActionButton, DeleteButton } from 'components/ui/buttons';
import { AdminPage } from 'components/ui/layout';
import { LoadingState, ErrorState } from 'components/ui/states';
import Tooltip from 'components/popups/Tooltip';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';
import { useNotification } from 'context/notification';

export default function HostPricing(props) {
    const [state, setState] = useState({
        connect_id: null,
        connect_type_id: null,
        data: null,
        status: 'loading', // loading, error, success, initialized (onboarding not started yet)
        error: null,
    });

    const [connectModalOpen, setConnectModalOpen] = useState(false);

    const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const [unlinked, setUnlinked] = useState(false);

    const { notify } = useNotification();

    const hostId = props.match.params.hostId;

    let hostName = '';

    // We initialize the host name via url params if available
    if (props.location.search.startsWith('?host=')) {
        hostName = decodeURIComponent(props.location.search.split('&')[0].slice(6));
    }

    // When the API call completes, we can get the host name from the results
    if (state.data?.host_name) {
        hostName = state.data.host_name;
    }

    const getConnectInfo = (logId) => {
        if (state.status !== 'loading') {
            setState((s) => ({ ...s, status: 'loading' }));
        }

        fetchExpresso(`/admin/hosts/${hostId}/connect${logId ? '?log=' + logId : ''}`)
            .then(async (res) => {
                if (res.status === 200) {
                    const { connect_id, connect_type_id, connect_info } = await res.json();
                    if (connect_id && connect_info) {
                        setState((state) => ({
                            ...state,
                            connect_id: connect_id,
                            connect_type_id: connect_type_id,
                            data: connect_info,
                            status: 'success',
                        }));
                    } else if (connect_id) {
                        setState((state) => ({
                            ...state,
                            connect_id: connect_id,
                            connect_type_id: connect_type_id,
                            data: null,
                            status: 'initialized',
                        }));
                    } else {
                        throw new Error('Invalid response from server');
                    }
                } else if (res.status === 404) {
                    // Host does not have a connect account
                    setState((state) => ({
                        ...state,
                        status: 'error',
                        error: 'This host does not have a connect account',
                    }));
                }
            })
            .catch(() =>
                setState((state) => ({ ...state, connect_id: null, data: null, status: 'error' })),
            );
    };

    useEffect(() => {
        getConnectInfo();
        // eslint-disable-next-line
    }, [hostId]);

    const unlinkConnectAccount = () => {
        setUnlinkModalOpen(false);
        setUnlinking(true);

        fetchExpresso(`/apiv2/hosts/${hostId}/connect`, {
            method: 'DELETE',
        })
            .then((res) => {
                if (res.status === 200) {
                    notify.success('Connect account unlinked successfully');
                    setUnlinked(true);
                } else {
                    notify.error('Failed to unlink connect account');
                }
            })
            .catch(() => notify.error('Failed to unlink connect account'))
            .finally(() => {
                setUnlinking(false);
            });
    };

    const copyEventObject = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(state.data.stripe_event_object));
            notify.success('Copied to clipboard!');
        } catch (e) {
            notify.warning('Unable to copy to clipboard');
        }
    };

    if (state.status === 'loading') {
        return <LoadingState />;
    }

    if (state.status === 'error') {
        return <ErrorState message={state.error || 'Unable to load connect account'} />;
    }

    if (state.status === 'initialized') {
        return (
            <AdminPage>
                <Card elevation={0}>
                    <CardHeader title={hostName} />
                    <CardContent>
                        <Grid container>
                            <Grid container item xs={12} md={6} lg={5} xl={4} spacing={1}>
                                <Grid item xs={6}>
                                    <Typography>Onboarding</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <GreyText>Not Started</GreyText>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography>Account Type</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <GreyText>
                                        {state.connect_type_id === 1 && 'Custom'}
                                        {state.connect_type_id === 2 && 'Express'}
                                        {state.connect_type_id === 3 && 'Standard'}
                                    </GreyText>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography>Charges</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <ErrorText>Disabled</ErrorText>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography>Payouts</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <ErrorText>Disabled</ErrorText>
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                    {unlinked === false && (
                        <CardActions>
                            <DeleteButton
                                disabled={unlinking}
                                onClick={() => setUnlinkModalOpen(true)}
                            >
                                Unlink Connect
                            </DeleteButton>
                        </CardActions>
                    )}
                </Card>

                <ConfirmAction
                    open={unlinkModalOpen}
                    destructive
                    onCancel={() => setUnlinkModalOpen(false)}
                    onConfirm={unlinkConnectAccount}
                    confirmText="Unlink"
                    title="Unlink Connect Account?"
                    description='This will return the host to "Legacy Payments". The connect account will be preserved, and any existing balance will need to be transfered to the FrontDoor+ Platform account before it can be deleted.'
                />
            </AdminPage>
        );
    }

    const { connect_id, connect_type_id } = state;
    const {
        last_updated_unix_timestamp,
        charges_enabled,
        payouts_enabled,
        onboarding_complete,
        requirements,
        future_requirements,
    } = state.data;
    const { page_count, current_page, last_page_id, next_page_id } = state.data;
    const { disabled_reason } = state.data.requirements;

    return (
        <AdminPage>
            <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                <IconButton
                    disabled={!last_page_id}
                    onClick={() => getConnectInfo(last_page_id)}
                    size="large"
                >
                    <NavigateBeforeIcon />
                </IconButton>
                <Box mx={2}>
                    <Typography>
                        {current_page}/{page_count}
                    </Typography>
                </Box>
                <IconButton
                    disabled={!next_page_id}
                    onClick={() => getConnectInfo(next_page_id)}
                    size="large"
                >
                    <NavigateNextIcon />
                </IconButton>
            </Box>
            <Card elevation={0}>
                <CardHeader
                    disableTypography
                    title={
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <Typography variant="h5">{hostName}</Typography>
                            <Tooltip
                                message={moment(last_updated_unix_timestamp).format(
                                    'MMM DD YYYY, h:mma',
                                )}
                            >
                                <Typography>
                                    {getRelativeTime(last_updated_unix_timestamp, 'short')}
                                </Typography>
                            </Tooltip>
                        </Box>
                    }
                    subheader={<Typography>{connect_id}</Typography>}
                />
                <CardContent>
                    <Grid container>
                        <Grid container item xs={12} md={6} lg={5} xl={4} spacing={1}>
                            <Grid item xs={6}>
                                <Typography>Account Type</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                {state.connect_type_id === 1 && 'Custom'}
                                {state.connect_type_id === 2 && 'Express'}
                                {state.connect_type_id === 3 && 'Standard'}
                            </Grid>
                            <Grid item xs={6}>
                                <Typography>Onboarding</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                {onboarding_complete ? (
                                    <SuccessText>Complete</SuccessText>
                                ) : (
                                    <GreyText>In Progress</GreyText>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <Typography>Charges</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                {charges_enabled ? (
                                    <SuccessText>Enabled</SuccessText>
                                ) : (
                                    <ErrorText>Disabled</ErrorText>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <Typography>Payouts</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                {payouts_enabled ? (
                                    <SuccessText>Enabled</SuccessText>
                                ) : (
                                    <ErrorText>Disabled</ErrorText>
                                )}
                            </Grid>
                            {disabled_reason !== null && (
                                <>
                                    <Grid item xs={6}>
                                        Disabled Reason
                                    </Grid>
                                    <Grid item xs={6}>
                                        {disabled_reason}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions>
                    {unlinked === false && (
                        <DeleteButton disabled={unlinking} onClick={() => setUnlinkModalOpen(true)}>
                            Unlink Connect
                        </DeleteButton>
                    )}
                    <ActionButton onClick={copyEventObject}>Copy JSON</ActionButton>
                    <ActionButton onClick={() => setConnectModalOpen(true)}>
                        View Connected Account
                    </ActionButton>
                </CardActions>
            </Card>
            <Box mb={2} />
            <RequirementList title="Required Documents" requirements={requirements} />
            <Box mb={2} />
            <RequirementList title="Future Requirements" requirements={future_requirements} />
            {connectModalOpen === true && (
                <ConnectDashboardLinks
                    connectId={connect_id}
                    connectTypeId={connect_type_id}
                    hostId={hostId}
                    onClose={() => setConnectModalOpen(false)}
                />
            )}
            <ConfirmAction
                open={unlinkModalOpen}
                destructive
                onCancel={() => setUnlinkModalOpen(false)}
                onConfirm={unlinkConnectAccount}
                confirmText="Unlink"
                title="Unlink Connect Account?"
                description='This will return the host to "Legacy Payments". The connect account will be preserved, and any existing balance will need to be transfered to the FrontDoor+ Platform account before it can be deleted.'
            />
        </AdminPage>
    );
}

function RequirementList(props) {
    const { title, requirements } = props;

    let upToDate = false;

    const {
        errors,
        past_due,
        alternatives,
        currently_due,
        eventually_due,
        disabled_reason,
        current_deadline,
        pending_verification,
    } = requirements;

    const subheader = current_deadline
        ? `Due ${moment(requirements.current_deadline * 1000).format('MMM DD, YYYY')}`
        : null;

    if (
        errors.length === 0 &&
        past_due.length === 0 &&
        alternatives.length === 0 &&
        currently_due.length === 0 &&
        eventually_due.length === 0 &&
        disabled_reason === null &&
        current_deadline === null &&
        pending_verification.length === 0
    ) {
        upToDate = true;
    }

    if (upToDate === true) {
        return null;
    }

    return (
        <Card elevation={0}>
            <CardHeader title={title} subheader={subheader || undefined} />
            <CardContent>
                {upToDate === true && (
                    <Typography>This host is up to date on all requirements</Typography>
                )}
                <Grid container spacing={2}>
                    <RequirementGroup
                        title="Past Due"
                        subheader="These requirements are overdue and should be submitted as soon as possible"
                        requirements={past_due}
                    />
                    <RequirementGroup
                        title="Currently Due"
                        subheader="Host must meet these requirements or their account may be disabled"
                        requirements={currently_due}
                    />
                    <RequirementGroup
                        title="Eventually Due"
                        subheader="These items will be required eventually"
                        requirements={eventually_due}
                    />
                    <RequirementGroup
                        title="Pending Verification"
                        subheader="These changes are being processed by Stripe"
                        requirements={pending_verification}
                    />
                    <ErrorGroup errors={errors} />
                </Grid>
            </CardContent>
        </Card>
    );
}

function RequirementGroup(props) {
    const { title, requirements } = props;

    if (requirements.length === 0) {
        return null;
    }

    return (
        <Grid item xs={12} md={6}>
            <Typography>
                <b>{title}</b>
            </Typography>
            {requirements.map((r) => (
                <Typography>{r}</Typography>
            ))}
        </Grid>
    );
}

function ErrorGroup(props) {
    const { errors } = props;

    if (errors.length === 0) {
        return null;
    }

    return (
        <Grid item xs={12}>
            <TableContainer component={Box}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Requirement</TableCell>
                            <TableCell>Error Message</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {errors.map((e) => (
                            <TableRow>
                                <TableCell>{e.requirement}</TableCell>
                                <TableCell>{e.reason}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}

function ConnectDashboardLinks(props) {
    const { connectId, connectTypeId, hostId, onClose } = props;

    const { notify } = useNotification();

    // *** Request Login Link for Connected Account *** //

    const [connectLink, setConnectLink] = useState(null);

    useEffect(() => {
        // Generate Login Link if host is on Express
        if (connectTypeId === 2) {
            fetchExpresso(`/apiv2/hosts/${hostId}/connect/${connectId}/login_link`)
                .then((response) => response.json())
                .then((data) => setConnectLink(data.url))
                .catch((e) => notify.error(e));
        }
        // eslint-disable-next-line
    }, []);

    // *** Generate Link to Platform Dashboard *** //

    const platformLink =
        process.env.REACT_APP_ENV === 'production'
            ? `https://dashboard.stripe.com/connect/accounts/${connectId}`
            : `https://dashboard.stripe.com/test/connect/accounts/${connectId}`;

    return (
        <Dialog open onClose={onClose}>
            <DialogTitle>Open Stripe Dashboard</DialogTitle>
            <DialogContent>
                <Typography>
                    Note: "Login to Host" will trigger a verification code to be sent to the host's
                    phone number. Ask the host for the code in order to login in as them.
                </Typography>
            </DialogContent>
            <DialogActions>
                <ActionButton
                    href={connectLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!connectLink}
                >
                    Login as Host
                </ActionButton>
                <ActionButton href={platformLink} target="_blank" rel="noopener noreferrer">
                    View as Admin
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
}

const ErrorText = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    fontWeight: 'bold',
}));

const SuccessText = styled(Typography)(({ theme }) => ({
    color: theme.palette.success.main,
    fontWeight: 'bold',
}));

const GreyText = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[500],
    fontWeight: 'bold',
    textDecoration: 'italic',
}));
